import {
  AccordeonArrow,
  Amex,
  ApplePay,
  CarteBancaire,
  Edenred,
  GooglePay,
  Mastercard,
  Visa,
  WarningOrange
} from "@assets";
import { RadioGroup } from "@atomic/molecules";
import { IPaymentTypes } from "@foodi/core";
import { GlobalState } from "@redux/rootReducer";
import React, { memo } from "react";
import { useSelector } from "react-redux";
import { I18n } from "react-redux-i18n";
import {
  AccordeonArrowStyle,
  DropdownRow,
  FlexDirectionRow,
  IconContainer,
  PaymentContainer,
  PaymentTextContainer,
  ProductDescription,
  SmallDescription,
  WarningMessage,
  WarningMessageContainer,
} from "./Cart.style";
import { OnSiteMethodPayment } from "./OnSiteMethodPayment";
import { View } from "react-native";

interface IOtherCards {
  edenredBalance: string | undefined;
  handleEdenredPayment: (id: string) => Promise<void>;
  paymentMethod: IPaymentTypes;
  isOtherCardOpen: boolean;
  setIsOtherCardOpen: React.Dispatch<React.SetStateAction<boolean>>;
  paymentTypes: IPaymentTypes[];
  setPaymentMethod: React.Dispatch<React.SetStateAction<string | undefined>>;
  handleTriggerQuotationAndPaymentMethod: (
    newPaymentMethod: string
  ) => Promise<void>;
  userHasBadge: boolean;
}

export const OtherCards: React.FC<IOtherCards> = memo(
  ({
    edenredBalance,
    handleEdenredPayment,
    paymentMethod,
    setIsOtherCardOpen,
    isOtherCardOpen,
    paymentTypes,
    setPaymentMethod,
    handleTriggerQuotationAndPaymentMethod,
    userHasBadge
  }) => {
    const { isSchool } = useSelector(
      (state: GlobalState) => state.auth?.userInfo || {}
    );

    const warningInfoMessages = {
      [IPaymentTypes.OnSite]: isSchool
        ? I18n.t("restaurantDetail.cart.payOnSiteSchoolInfo")
        : I18n.t("restaurantDetail.cart.payOnSiteInfo"),
      [IPaymentTypes.Edenred]: I18n.t("restaurantDetail.cart.noSubventions"),
      [IPaymentTypes.Badge]: undefined,
      [IPaymentTypes.CreditCard]: undefined,
      [IPaymentTypes.Amex]: undefined,
      [IPaymentTypes.GooglePay]: undefined,
      [IPaymentTypes.ApplePay]: undefined,
    };

    const warningMsg: string | undefined = warningInfoMessages[paymentMethod];

    const hasCreditCard = paymentTypes.includes(IPaymentTypes.CreditCard);
    const hasAmex = paymentTypes.includes(IPaymentTypes.Amex);
    const hasGooglePay = paymentTypes.includes(IPaymentTypes.GooglePay);
    const hasApplePay = paymentTypes.includes(IPaymentTypes.ApplePay);

    const alwaysOpen = !userHasBadge && (hasCreditCard || hasAmex || hasGooglePay || hasApplePay);
    return (
      <>
        {!alwaysOpen && <DropdownRow onPress={() => setIsOtherCardOpen((state) => !state)}>
          <ProductDescription>
            {I18n.t("restaurantDetail.cart.otherCards")}
          </ProductDescription>
          <AccordeonArrowStyle rotate={isOtherCardOpen}>
            <AccordeonArrow />
          </AccordeonArrowStyle>
        </DropdownRow>}
        {(isOtherCardOpen || alwaysOpen) && (
          <>
            {(hasCreditCard || hasAmex || hasGooglePay || hasApplePay) && (
              <>
                <PaymentContainer>
                  <FlexDirectionRow>
                    <div style={{ marginTop: 4}}>
                      <CarteBancaire width={25} height={17} />
                    </div>
                    <PaymentTextContainer>
                      <ProductDescription>
                        {I18n.t("restaurantDetail.cart.directPayment")}
                      </ProductDescription>
                        <FlexDirectionRow style={{ paddingTop: 7 }}>
                          {hasCreditCard && <>
                              <div style={{ paddingRight: 7 }}><CarteBancaire width={20} height={15} /></div>
                              <div style={{ paddingRight: 7 }}><Mastercard width={21} height={15}/></div>
                              <div style={{ paddingRight: 7 }}><Visa width={27} height={15}/></div>
                              </>
                          }
                          {hasAmex && <div style={{ paddingRight: 7 }}><Amex width={20} height={15}/></div>}
                          {hasGooglePay && <div style={{ paddingRight: 7 }}><GooglePay width={35} height={16}/></div>}
                          {hasApplePay && <div><ApplePay width={35} height={16}/></div>}
                        </FlexDirectionRow>
                    </PaymentTextContainer>
                  </FlexDirectionRow>
                  <RadioGroup
                    radioButtonsProps={[{ id: IPaymentTypes.CreditCard }]}
                    value={paymentMethod}
                    onPress={(paymentMethod) => {
                      // Credit Card is explicitly set, because currently through foodi is not possible to distinguish Credit Card of Google/Apple Pay
                      // The user makes this choice inside Mercanet PayPage (in backend it's identified which option was chosen)
                      setPaymentMethod(IPaymentTypes.CreditCard)
                    }}
                  />
                </PaymentContainer>
                <WarningMessageContainer marginTop={0} backgroundColor={'lightyellow'}>
                  <WarningOrange />
                  <WarningMessage>{I18n.t("restaurantDetail.cart.noSubventions")}</WarningMessage>
                </WarningMessageContainer>
              </>
            )}
            {paymentTypes.includes(IPaymentTypes.Edenred) && (
              <>
                <PaymentContainer>
                  <FlexDirectionRow>
                    <IconContainer>
                      <Edenred />
                    </IconContainer>
                    <PaymentTextContainer>
                      <ProductDescription>
                        {I18n.t("restaurantDetail.cart.edenred")}
                      </ProductDescription>
                      <FlexDirectionRow>
                        <SmallDescription>
                          {I18n.t("restaurantDetail.cart.pay")}
                        </SmallDescription>
                        <SmallDescription hasMargin bold>
                          {edenredBalance ? `${edenredBalance}€` : "..."}
                        </SmallDescription>
                      </FlexDirectionRow>
                    </PaymentTextContainer>
                  </FlexDirectionRow>
                  <RadioGroup
                    radioButtonsProps={[{ id: IPaymentTypes.Edenred }]}
                    value={paymentMethod}
                    onPress={handleEdenredPayment}
                  />
                </PaymentContainer>
              </>
            )}
            {paymentTypes.includes(IPaymentTypes.OnSite) && (
              <OnSiteMethodPayment
                paymentMethod={paymentMethod}
                setPaymentMethod={setPaymentMethod}
                handleTriggerQuotationAndPaymentMethod={
                  handleTriggerQuotationAndPaymentMethod
                }
              />
            )}
            {warningMsg && (
              <WarningMessageContainer>
                <WarningOrange />
                <WarningMessage>{warningMsg}</WarningMessage>
              </WarningMessageContainer>
            )}
          </>
        )}
      </>
    );
  }
);
